/*
  Sunnest cart drawer – line actions for snippets/cart-drawer.liquid.

  Uses Shopify's Ajax Cart API (/cart/change.js) with Section Rendering
  ("cart-drawer", "cart-icon-bubble"), the same sections Origin's <cart-drawer> and the
  GemPages add-to-cart button render. Prices are never calculated here: after every
  change the drawer is re-rendered from Shopify's Liquid output.
*/
if (!customElements.get('sunnest-cart-drawer')) {
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const ERROR_MESSAGE = 'Vi kunne ikke oppdatere handlekurven din. Prøv igjen.';
  // One cart request at a time: line keys change when a selling plan changes, and
  // overlapping requests could otherwise act on stale lines.
  let requestInFlight = false;

  class SunnestCartDrawer extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      // Refresh when another part of the theme changes the cart (e.g. quick add).
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || ['sunnest-cart', 'product-form'].includes(event.source)) return;
          this.refresh();
        });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      if (this.unsubscribe) this.unsubscribe();
    }

    get root() {
      return Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    }

    onClick(event) {
      if (requestInFlight && event.target.closest('[data-sc-qty], [data-sc-remove], [data-sc-promo]')) {
        event.preventDefault();
        return;
      }

      // Sunnest Club promo: the whole box (and its CTA) switches the line to the monthly Club plan.
      const promo = event.target.closest('[data-sc-promo]');
      if (promo) {
        const line = promo.closest('[data-sc-line]');
        this.change(
          line,
          { quantity: Number(line.dataset.quantity), selling_plan: Number(promo.dataset.scPromo) },
          line.querySelector('[data-sc-plan]')
        );
        return;
      }

      const qtyButton = event.target.closest('[data-sc-qty]');
      if (qtyButton) {
        const line = qtyButton.closest('[data-sc-line]');
        const next = Math.max(0, Number(line.dataset.quantity) + Number(qtyButton.dataset.scQty));
        this.change(line, { quantity: next }, qtyButton);
        return;
      }

      const removeButton = event.target.closest('[data-sc-remove]');
      if (removeButton) this.change(removeButton.closest('[data-sc-line]'), { quantity: 0 }, null);
    }

    onChange(event) {
      const line = event.target.closest('[data-sc-line]');
      if (!line) return;

      if (requestInFlight) {
        // Undo the control change; the next render shows the real cart state.
        if (event.target.matches('[data-sc-plan]')) event.target.value = event.target.dataset.current || '';
        if (event.target.matches('[data-sc-qty-input]')) event.target.value = line.dataset.quantity;
        return;
      }

      if (event.target.matches('[data-sc-plan]')) {
        const value = event.target.value;
        this.change(
          line,
          { quantity: Number(line.dataset.quantity), selling_plan: value ? Number(value) : null },
          event.target
        );
        return;
      }

      if (event.target.matches('[data-sc-qty-input]')) {
        const quantity = parseInt(event.target.value, 10);
        if (Number.isNaN(quantity) || quantity < 0) {
          event.target.value = line.dataset.quantity;
          return;
        }
        this.change(line, { quantity }, event.target);
      }
    }

    async change(line, changes, focusTarget) {
      if (!line) return;
      const body = { id: line.dataset.key, ...changes };
      const focusSelector = focusTarget ? this.selectorFor(focusTarget) : null;
      const result = await this.request('cart/change.js', body, line);
      if (!result) return;
      this.render(result.sections, focusSelector);
      this.announce(changes.quantity === 0 ? 'Varen er fjernet fra handlekurven' : 'Handlekurven er oppdatert');
      this.publish(result);
    }

    async request(path, body, busyElement) {
      if (requestInFlight) return null;
      requestInFlight = true;
      this.setLoading(true, busyElement);
      this.showError('');
      try {
        const response = await fetch(this.root + path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || ERROR_MESSAGE, busyElement);
          return null;
        }
        if (!data.sections || !data.sections['cart-drawer']) {
          // Sections unavailable: fall back to a fresh render.
          await this.refresh();
          return null;
        }
        return data;
      } catch (error) {
        console.error('[Sunnest cart]', error);
        this.showError(ERROR_MESSAGE, busyElement);
        return null;
      } finally {
        requestInFlight = false;
        if (this.isConnected) this.setLoading(false, busyElement);
      }
    }

    async refresh() {
      try {
        const response = await fetch(`${this.root}?sections=${SECTIONS.join(',')}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.render(await response.json(), null);
      } catch (error) {
        console.error('[Sunnest cart]', error);
        this.showError(ERROR_MESSAGE);
      }
    }

    // Same DOM update as Origin's CartDrawer.renderContents(), without re-opening the drawer.
    render(sections, focusSelector) {
      const drawer = this.closest('cart-drawer');
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
        if (bubble && source) bubble.innerHTML = source.innerHTML;
      }

      const source = parse(sections['cart-drawer']).querySelector('#CartDrawer');
      const target = document.getElementById('CartDrawer');
      if (!source || !target || !drawer) return;

      const isEmpty = !source.querySelector('[data-sc-line]');
      drawer.classList.toggle('is-empty', isEmpty);
      target.innerHTML = source.innerHTML;

      const overlay = document.getElementById('CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', () => drawer.close());

      const focusElement =
        (focusSelector && target.querySelector(focusSelector)) ||
        target.querySelector(isEmpty ? '.sunnest-cart__empty a' : '.sunnest-cart__close');
      if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(isEmpty ? target.querySelector('.drawer__inner-empty') : target, focusElement);
      }
    }

    selectorFor(element) {
      const line = element.closest('[data-sc-line]');
      if (!line || !line.id) return null;
      if (element.matches('[data-sc-plan]')) return `#${line.id} [data-sc-plan]`;
      if (element.matches('[data-sc-qty-input]')) return `#${line.id} [data-sc-qty-input]`;
      if (element.matches('[data-sc-qty]')) return `#${line.id} [data-sc-qty="${element.dataset.scQty}"]`;
      return null;
    }

    setLoading(isLoading, element) {
      const app = document.querySelector('sunnest-cart-drawer');
      if (!app) return;
      app.classList.toggle('is-loading', isLoading);
      app.setAttribute('aria-busy', String(isLoading));
      const line = element && element.closest('[data-sc-line]');
      if (line) line.classList.toggle('is-loading', isLoading);
    }

    showError(message, element) {
      const app = document.querySelector('sunnest-cart-drawer') || this;
      const line = element && element.closest('[data-sc-line]');
      const lineError = line && line.querySelector('[data-sc-line-error]');
      const globalError = app.querySelector('[data-sc-error]');
      app.querySelectorAll('[data-sc-line-error]').forEach((node) => (node.textContent = ''));
      if (lineError && message) {
        lineError.textContent = message;
        if (globalError) globalError.hidden = true;
        return;
      }
      if (globalError) {
        globalError.textContent = message;
        globalError.hidden = !message;
      }
    }

    announce(message) {
      const status = document.querySelector('sunnest-cart-drawer [data-sc-status]');
      if (status) status.textContent = message;
    }

    publish(cartData) {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sunnest-cart', cartData });
      }
    }
  }

  customElements.define('sunnest-cart-drawer', SunnestCartDrawer);
}
